import { rootApi } from '../rootApi';

// Helper function to get current language ID
const getCurrentLanguageIdFromParams = (params) => {
    // If LanguageId is explicitly provided, use it
    if (params?.LanguageId !== undefined) {
        return params.LanguageId;
    }
    // If languageId is explicitly provided, use it
    if (params?.languageId !== undefined) {
        return params.languageId;
    }
    // Default fallback
    return 1;
};

export const compoundApi = rootApi.injectEndpoints({
    endpoints: (builder) => ({
        getChassisDropdownList: builder.query({
            query: (params) => {
                // Base parameters
                const queryParams = {
                    LanguageId: getCurrentLanguageIdFromParams(params),
                    RowCount: params?.RowCount !== undefined ? params.RowCount : 30,
                    SearchText: params?.SearchText || ''
                };

                return {
                    url: 'v1/compound/ChassisList',
                    params: queryParams
                };
            },
            transformResponse: (response) => {
                // Check if response has the expected structure with data property
                if (response && typeof response === 'object') {
                    if (response.data && Array.isArray(response.data)) {
                        return response.data;
                    }

                    // If response itself is an array, return it directly
                    if (Array.isArray(response)) {
                        return response;
                    }
                }

                // Fallback: return empty array if structure doesn't match
                return [];
            },
            providesTags: ['Compound']
        }),
        getFaultModesDropdownList: builder.query({
            query: (params = {}) => ({
                url: 'v1/compound/getfaultmodesdropdown',
                params: { LanguageId: getCurrentLanguageIdFromParams(params) }
            }),
            transformResponse: (response) => {
                // Beklenen response örneğine göre data property alınır
                if (response && typeof response === 'object' && response.isSuccessful && Array.isArray(response.data)) {
                    return response.data;
                }

                return [];
            },
            providesTags: ['Compound']
        }),
        getLaborListDropdown: builder.query({
            query: (params = {}) => ({
                url: 'v1/compound/getlaborlistdropdown',
                params: {
                    LanguageId: getCurrentLanguageIdFromParams(params),
                    chassisNo: params?.chassisNo || '',
                    searchText: params?.searchText || '',
                    pageSize: params?.pageSize || 20,
                    ...(params?.claimId ? { claimId: params.claimId } : {})
                }
            }),
            transformResponse: (response) => {
                // Beklenen response örneğine göre data property alınır
                if (response && typeof response === 'object' && response.isSuccessful && Array.isArray(response.data)) {
                    return response.data;
                }

                return [];
            },
            providesTags: ['Compound']
        }),

        getDealerAgreementType: builder.query({
            query: (params = {}) => ({
                url: 'v1/compound/getdealeragreementtype',
                method: 'GET',
                params: params?.claimId ? { claimId: params.claimId } : undefined
            }),
            transformResponse: (response) => {
                if (response && typeof response === 'object') {
                    if (response.data) {
                        return response.data;
                    }
                }

                return null;
            },
            providesTags: ['Compound']
        }),

        getDealerPackageDropdown: builder.query({
            query: (params) => {
                const queryParams = {
                    pageSize: params?.pageSize || 100,
                    searchText: params?.searchText || ''
                };

                return {
                    url: 'v1/compound/getdealerpackagedropdown',
                    method: 'GET',
                    params: queryParams
                };
            },
            transformResponse: (response) => {
                if (response && typeof response === 'object') {
                    if (response.data && Array.isArray(response.data)) {
                        return response.data;
                    }

                    if (Array.isArray(response)) {
                        return response;
                    }
                }

                return [];
            },
            providesTags: ['Compound']
        }),

        getSapBulletinByChassisNo: builder.query({
            query: (params) => {
                const chassisNo = params?.ChassisNo || params;
                const queryParams = { ChassisNo: chassisNo };
                if (typeof params === 'object' && params?.OnlyOpen) {
                    queryParams.OnlyOpen = true;
                }
                return {
                    url: 'v1/compound/getsapbulletinbychassisno',
                    method: 'GET',
                    params: queryParams
                };
            },
            transformResponse: (response) => {
                if (response && typeof response === 'object' && response.isSuccessful && Array.isArray(response.data)) {
                    return response.data;
                }
                return [];
            },
            providesTags: ['Compound']
        }),

        getChassisDropdownListWithExternal: builder.query({
            query: (params) => {
                const queryParams = {
                    LanguageId: getCurrentLanguageIdFromParams(params),
                    RowCount: params?.RowCount !== undefined ? params.RowCount : 30,
                    SearchText: params?.SearchText || ''
                };

                return {
                    url: 'v1/compound/ChassisListWithExternal',
                    params: queryParams
                };
            },
            transformResponse: (response) => {
                if (response && typeof response === 'object') {
                    if (response.data && Array.isArray(response.data)) {
                        return response.data.map((item) => ({
                            ...item,
                            motorNumber: item.motorNumber || null,
                            plateNumber: item.plateNumber || item.licensePlate || null
                        }));
                    }

                    if (Array.isArray(response)) {
                        return response.map((item) => ({
                            ...item,
                            motorNumber: item.motorNumber || null,
                            plateNumber: item.plateNumber || item.licensePlate || null
                        }));
                    }
                }

                return [];
            },
            providesTags: ['Compound']
        }),
        getForeignMainDealerDropdown: builder.query({
            query: (params = {}) => ({
                url: 'v1/compound/GetForeignMainDealer',
                method: 'GET',
                params: {
                    SearchText: params?.SearchText ?? '',
                    ItemCount: params?.ItemCount ?? 50
                }
            }),
          transformResponse: (response) => extractArrayData(response),
            providesTags: ['Compound']
        }),
        getSapDealerByCountryDropdown: builder.query({
            query: (params = {}) => ({
                url: 'v1/compound/GetSapDealerByCountry',
                method: 'GET',
                params: {
                    CountryCode: params?.CountryCode ?? 'TR',
                    SearchText: params?.SearchText ?? '',
                    ItemCount: params?.ItemCount ?? 50
                }
            }),
           transformResponse: (response) => extractArrayData(response),
            providesTags: ['Compound']
        }),
        getCanUseFullServiceDealerDropdown: builder.query({
            query: ({ ItemCount = 50, SearchText = '' } = {}) => ({
                url: 'v1/compound/GetCanUseFullService',
                params: {
                    ItemCount,
                    SearchText
                }
            }),
           transformResponse: (response) => extractArrayData(response),
            providesTags: ['Compound']
        })
    })
});

export const {
    useGetChassisDropdownListQuery,
    useGetFaultModesDropdownListQuery,
    useGetLaborListDropdownQuery,
    useGetDealerAgreementTypeQuery,
    useGetDealerPackageDropdownQuery,
    useGetSapBulletinByChassisNoQuery,
    useLazyGetSapBulletinByChassisNoQuery,
    useGetChassisDropdownListWithExternalQuery,
    useLazyGetChassisDropdownListWithExternalQuery,
    useGetForeignMainDealerDropdownQuery,
    useGetSapDealerByCountryDropdownQuery,
    useLazyGetSapDealerByCountryDropdownQuery,
    useGetCanUseFullServiceDealerDropdownQuery
} = compoundApi;


export const extractArrayData = (response) => {
    if (!response || typeof response !== 'object') return [];

    // data.items pattern
    if (Array.isArray(response?.data?.items)) {
        return response.data.items;
    }

    // isSuccessful + data.items pattern
    if (response?.isSuccessful && Array.isArray(response?.data?.items)) {
        return response.data.items;
    }

    // data array pattern
    if (Array.isArray(response?.data)) {
        return response.data;
    }

    // direct array pattern
    if (Array.isArray(response)) {
        return response;
    }

    return [];
};